import React, { useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Upload, FileText, CircleAlert, CircleCheck, Clock } from "lucide-react";

const DOC_LABELS = {
  dni: "DNI / Pasaporte",
  certificate: "Certificado profesional",
  insurance: "Seguro de responsabilidad civil",
  other: "Otro documento",
};

export default function KYCPage() {
  const [data, setData] = useState({ documents: [], status: "none" });
  const [kind, setKind] = useState("dni");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = () => api.get("/kyc/mine").then(r => setData(r.data)).catch(e => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/kyc/upload?doc_type=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documento subido. Nuestro equipo lo revisará.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const statusColor = { none: "bg-gray-100 text-gray-700", pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

  return (
    <div className="min-h-screen hub-cream">
      <Navbar/>
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="hub-pill hub-sand text-[#0B1D3A] mb-3"><ShieldCheck size={12}/> Verificación</div>
        <h1 className="font-serif text-5xl text-[#0B1D3A]">Consigue el sello Verificado.</h1>
        <p className="text-[#5C6B7D] mt-2 max-w-xl">Sube tus documentos y aparecerás con el sello verde que dispara la confianza de los clientes.</p>

        <div className="hub-card p-6 mt-8 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-display text-[#5C6B7D]">Estado actual</div>
            <div className="mt-1"><span className={`hub-pill ${statusColor[data.status] || statusColor.none}`}>
              {data.status === "approved" ? "Verificado ✓" : data.status === "pending" ? "En revisión" : data.status === "rejected" ? "Rechazado" : "Sin verificar"}
            </span></div>
          </div>
        </div>

        <div className="hub-card p-6 mt-6">
          <h3 className="font-serif text-2xl">Subir documento</h3>
          <p className="text-sm text-[#5C6B7D] mt-1">PDF o imagen. Máx 10 MB. Los datos son confidenciales y solo los ve el equipo de MarinaHub.</p>
          <div className="mt-4 flex gap-2">
            <select value={kind} onChange={e=>setKind(e.target.value)} className="text-sm h-10 border border-[#0B1D3A]/15 rounded-full px-4" data-testid="kyc-doctype">
              {Object.entries(DOC_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input ref={inputRef} type="file" accept="image/*,application/pdf" hidden onChange={e=>upload(e.target.files)} data-testid="kyc-input"/>
            <Button onClick={()=>inputRef.current?.click()} disabled={uploading} className="hub-btn-primary gap-2 rounded-full" data-testid="kyc-upload-btn">
              <Upload size={14}/> {uploading ? "Subiendo..." : "Elegir archivo"}
            </Button>
          </div>
        </div>

        <h2 className="font-serif text-2xl text-[#0B1D3A] mt-10 mb-3">Mis documentos</h2>
        <div className="hub-card divide-y divide-[#0B1D3A]/5">
          {data.documents.length === 0 && <div className="p-8 text-center text-[#5C6B7D] text-sm">No has subido nada aún.</div>}
          {data.documents.map(d => (
            <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`kyc-doc-${d.id}`}>
              <FileText className="text-[#5C6B7D]"/>
              <div className="flex-1">
                <div className="font-display font-semibold text-sm">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
                <div className="text-xs text-[#5C6B7D]">{new Date(d.created_at).toLocaleString("es-ES")}</div>
              </div>
              <span className={`hub-pill ${statusColor[d.status] || statusColor.none} flex items-center gap-1`}>
                {d.status === "approved" ? <CircleCheck size={11}/> : d.status === "rejected" ? <CircleAlert size={11}/> : <Clock size={11}/>}
                {d.status}
              </span>
            </div>
          ))}
        </div>

        <ProVideoSection/>
      </main>
    </div>
  );
}

function ProVideoSection() {
  const [uploading, setUploading] = React.useState(false);
  const [me, setMe] = React.useState(null);
  const inp = React.useRef();
  React.useEffect(() => { api.get("/auth/me").then(r => setMe(r.data)); }, []);
  const upload = async (files) => {
    if (!files?.length) return;
    if (files[0].size > 50*1024*1024) { toast.error("Máx 50 MB. Comprime el vídeo."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", files[0]);
      await api.post("/professionals/me/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vídeo subido a tu perfil público");
      const r = await api.get("/auth/me"); setMe(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const remove = async () => {
    await api.delete("/professionals/me/video");
    const r = await api.get("/auth/me"); setMe(r.data); toast.success("Vídeo eliminado");
  };
  const vp = me?.professional?.video_path;
  return (
    <div className="mt-10">
      <h2 className="font-serif text-2xl text-[#0B1D3A] mb-3">Presentación en vídeo</h2>
      <div className="hub-card p-6">
        <p className="text-sm text-[#5C6B7D]">Sube un vídeo corto (máx 30s, 50 MB) para que los clientes te vean en tu perfil público. Diferénciate en redes.</p>
        {vp && (
          <video src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${vp}`} controls playsInline
            className="w-full max-w-md rounded-xl border border-[#0B1D3A]/10 mt-4"/>
        )}
        <div className="mt-4 flex gap-2">
          <input ref={inp} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={e=>upload(e.target.files)} data-testid="pro-video-input"/>
          <Button onClick={()=>inp.current?.click()} disabled={uploading} className="hub-btn-primary gap-2 rounded-full" data-testid="pro-video-upload-btn">
            <Upload size={14}/> {uploading ? "Subiendo..." : vp ? "Reemplazar vídeo" : "Subir vídeo"}
          </Button>
          {vp && <Button variant="ghost" onClick={remove} className="text-red-500" data-testid="pro-video-delete-btn">Eliminar</Button>}
        </div>
      </div>
    </div>
  );
}
